import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import { BaseEntity } from './base.entity';
import { ClientPublicUserEntity } from './client-public-user.entity';
import { CompanyEntity } from './company.entity';
import { OrderEntity } from './order.entity';
import { PublicUserSelectableResourceEntity } from './public-user-selectable-resource.entity';

// Public users are scoped to a single company, so email/phone uniqueness is
// per-company: the same person can be a buyer under two different companies
// (two public_user rows). The legacy (value, _fk__object__id_reference,
// _referenceId) scoping made these effectively globally unique because those
// ref columns default to 0 — wrong for the per-company model. The old indexes
// are dropped by 1780933992671-UpdatePublicUserUniqueConstraintsToCompany.
@Index('_user__email_company_unique', ['email', 'fkCompanyId'], {
	unique: true,
})
@Index('_user__phone_company_unique', ['phoneNumber', 'fkCompanyId'], {
	unique: true,
})
@Entity('public_user')
export class PublicUserEntity extends BaseEntity {
	@Column('int', { name: 'workd__user__id', unsigned: true })
	public workdUserId!: number;

	@Column('varchar', { name: '_firstName', nullable: true, length: 255 })
	public firstName!: string | null;

	@Column('varchar', { name: '_lastName', nullable: true, length: 255 })
	public lastName!: string | null;

	@Column('varchar', { name: '_email', nullable: true, length: 255 })
	public email!: string | null;

	@Column('varchar', { name: '_phoneNumber', nullable: true, length: 16 })
	public phoneNumber!: string | null;

	@Column('int', {
		name: '_fk__object__id_reference',
		unsigned: true,
		default: () => "'0'",
	})
	public fkObjectIdReference!: number;

	@Column('int', { name: '_referenceId', unsigned: true, default: () => "'0'" })
	public referenceId!: number;

	// Modern replacement for the (_fk__object__id_reference, _referenceId)
	// pair when the reference is to a company — which is the only thing
	// portal users actually point at. Unlike DVP `_user.fk_company__id`
	// (which mixes private/public users and defaults to 0), every row in
	// `public_user` is by definition company-scoped, so:
	//   - Default is 1, which is the correct value for the overwhelmingly
	//     common single-company tenant (existing portal users on those
	//     tenants need no backfill at all).
	//   - It's a real FK to `company._id` because the company table is in
	//     the tenant DB right alongside this one.
	@ManyToOne(() => CompanyEntity, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_company__id', referencedColumnName: 'id' }])
	public fkCompany!: CompanyEntity;

	@Index('_user__fk__company__id', ['fkCompanyId'])
	@Column('int', {
		name: 'fk_company__id',
		unsigned: true,
		default: () => "'1'",
	})
	public fkCompanyId!: number;

	@OneToMany(
		() => ClientPublicUserEntity,
		(clientPublicUser) => clientPublicUser.fkPublicUser,
		{},
	)
	public clientPublicUsers!: Array<ClientPublicUserEntity>;

	@OneToMany(() => OrderEntity, (order) => order.submittedByUser, {})
	public orders!: Array<OrderEntity>;

	@OneToMany(() => OrderEntity, (order) => order.fkPublicUserIdOverride2, {})
	public orders2!: Array<OrderEntity>;

	@OneToMany(
		() => PublicUserSelectableResourceEntity,
		(publicUserSelectableResource) => publicUserSelectableResource.fkPublicUser,
		{},
	)
	public publicUserSelectableResources!: Array<PublicUserSelectableResourceEntity>;
}
